import React, { useMemo } from 'react';
import { type MultiPageVideoModel } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { Button, Flex } from '@bynder/design-system';
import { IconPause, IconPlay, IconReplay, IconSkipNext, IconSkipPrevious } from '@bynder/icons';
import { convertFrameToTime } from 'packages/common/editor/timeline/helpers/frame-to-time-converter';
import VolumeControl from 'packages/pages/components/VolumeControl';
import { DesignTab } from 'packages/pages/design/sidebar/types';
import { EllapsedTime, TimelineHeaderContainer, TotalTime } from './Component.styled';

type Props = {
    isPlaying: boolean;
    currentFrame: number;
    togglePlayback: () => void;
    toNextShot: () => void;
    toPrevShot: () => void;
    selectedShotIndex: number;
    shots: [];
    duration: number;
    frameRate: number;
    creativeModel: MultiPageVideoModel;
    designType: 'TEMPLATE' | 'CREATIVE';
    className?: string;
    setDesignTab: (tab: DesignTab) => void;
};

const TimelineHeader = ({
    isPlaying,
    currentFrame,
    togglePlayback,
    toNextShot,
    toPrevShot,
    selectedShotIndex,
    shots,
    duration,
    frameRate,
    creativeModel,
    designType,
    className = '',
}: Props) => {
    const { translate } = useTranslate();

    const convertedDuration = useMemo(() => convertFrameToTime(duration, frameRate), [duration, frameRate]);

    const getSliderSize = () => {
        if (designType === 'CREATIVE') {
            return 'm';
        }

        return 's';
    };

    return (
        <TimelineHeaderContainer className={`${className || ''}`}>
            <Flex alignItems="center">
                <EllapsedTime>{convertFrameToTime(currentFrame, frameRate)}</EllapsedTime>
                &nbsp;/&nbsp;
                <TotalTime>{convertedDuration}</TotalTime>
            </Flex>
            <Flex alignItems="center">
                <Button
                    variant="clean"
                    title={translate('editor.timeline.shots.previous')}
                    isDisabled={!currentFrame}
                    onClick={toPrevShot}
                    icon={<IconSkipPrevious />}
                />
                <Button
                    title={translate('editor.timeline.shots.play')}
                    onClick={togglePlayback}
                    variant="clean"
                    icon={isPlaying ? <IconPause /> : currentFrame === duration - 1 ? <IconReplay /> : <IconPlay />}
                />
                <Button
                    title={translate('editor.timeline.shots.next')}
                    isDisabled={
                        (!isPlaying && selectedShotIndex === shots.length - 1) ||
                        (isPlaying && selectedShotIndex === shots.length - 1)
                    }
                    onClick={toNextShot}
                    variant="clean"
                    icon={<IconSkipNext />}
                />
            </Flex>
            <VolumeControl size={getSliderSize()} creativeModel={creativeModel} />
        </TimelineHeaderContainer>
    );
};

export default TimelineHeader;
